import { useEffect, useState } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import {
  Box, Chip, Pagination, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Button,
  Typography, IconButton
} from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DownloadIcon from '@mui/icons-material/Download';
import PropTypes from "prop-types";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FormControl, Select, MenuItem, InputLabel } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { getEmpresas } from "../../actions/empresas";

dayjs.extend(isBetween);

import { isEmpty } from "../../components/common/helpers";
import { getServicioArrayId } from '../../actions/servicios';
import UploadComprobanteModal from '../modals/UploadComprobanteModal';

const getBadge = (statusType) => {
  switch (statusType) {
    case 1:
      return <Chip className="badge_status" color="success" size="small" label="Completado" />;
    case 0:
      return <Chip className="badge_status" color="warning" size="small" label="En Proceso" />;
    case 2:
      return <Chip className="badge_status" color="error" size="small" label="Fallido" />;
  }
};

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order) {
  return order === "asc"
    ? (a, b) => descendingComparator(a, b, "date")
    : (a, b) => -descendingComparator(a, b, "date");
}

const TableInv = ({ history, moment, onSelect }) => {
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState("asc");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [operaciones, setOperaciones] = useState([]);
  const [comprobanteItem, setComprobanteItem] = useState(null);

  const [dateFilter, setDateFilter] = useState("all");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const dispatch = useDispatch();
  const empresas = useSelector(state => state.empresas.empresas);

  const totalPages = Math.ceil(history.length / rowsPerPage);

  useEffect(() => {
    if (isEmpty(empresas)) {
      dispatch(getEmpresas());
    }
  }, []);

  useEffect(() => {
    if (!isEmpty(history)) {
      getNameS()
    }
  }, [history]);

  const getNameS = async () => {
    const res = await getServicioArrayId(history);
    setOperaciones(res)
    return;
  }

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const createSortHandler = (event) => {
    const isAsc = order === "asc";
    setOrder(isAsc ? "desc" : "asc");
  };

  const handleDateFilterChange = (event) => {
    const value = event.target.value;
    setDateFilter(value);
    const now = dayjs();

    if (value === "this-month") {
      setStartDate(now.startOf('month'));
      setEndDate(now.endOf('month'));
    } else if (value === "last-month") {
      setStartDate(now.subtract(1, 'month').startOf('month'));
      setEndDate(now.subtract(1, 'month').endOf('month'));
    } else if (value === "last-7-days") {
      setStartDate(now.subtract(7, 'days'));
      setEndDate(now);
    } else if (value === "all") {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const sortedAndPaginatedOperaciones = operaciones
    .filter(op => {
      if (!startDate || !endDate) return true;
      const opDate = dayjs(op.date);
      return opDate.isBetween(startDate, endDate, 'day', '[]');
    })
    .sort(getComparator(order));

  const handleDownload = async (file) => {
    try {
      if (Array.isArray(file) && file.length > 1) {
        // Lógica para descargar ZIP si hay múltiples archivos
        const zip = new JSZip();
        const promises = file.map(async (url, index) => {
          const response = await fetch(`${url}?t=${new Date().getTime()}`, { cache: "reload" });
          const blob = await response.blob();
          // Obtener extensión o usar default
          let ext = 'jpg';
          const fileNameFromUrl = url.split('/').pop().split('?')[0].toLowerCase();

          if (blob.type === 'application/pdf') ext = 'pdf';
          else if (blob.type === 'image/png') ext = 'png';
          else if (blob.type === 'image/jpeg') ext = 'jpg';
          else if (blob.type === 'text/xml' || blob.type === 'application/xml' || fileNameFromUrl.endsWith('_xml')) ext = 'xml';
          else {
            // Fallback: try to get extension from URL but ensure no slashes
            const parts = fileNameFromUrl.split('.');
            if (parts.length > 1) {
              ext = parts.pop();
            }
          }
          zip.file(`comprobante_${index + 1}.${ext}`, blob);
        });

        await Promise.all(promises);

        const content = await zip.generateAsync({ type: "blob" });
        saveAs(content, "documentos.zip");
      } else {
        // Lógica original para un solo archivo (string o array de 1 elemento)
        const fileUrl = Array.isArray(file) ? file[0] : file;

        // Agregar un parámetro único para evitar la caché
        const uniqueUrl = `${fileUrl}?t=${new Date().getTime()}`;

        const response = await fetch(uniqueUrl, { cache: "reload" }); // Evitar caché
        const blob = await response.blob();

        // Extraer nombre original de la URL
        let fileName = fileUrl.split('/').pop().split('?')[0];

        // Asegurar extensión XML si es necesario (mejorado con check de sufijo _xml)
        if (blob.type === 'text/xml' || blob.type === 'application/octet-stream' || fileName.toLowerCase().endsWith('_xml')) {
          if (!fileName.toLowerCase().endsWith('.xml')) {
            // Reemplazar _xml por .xml si existe al final, o simplemente añadirlo
            if (fileName.toLowerCase().endsWith('_xml')) {
              fileName = fileName.substring(0, fileName.length - 4) + '.xml';
            } else {
              fileName += '.xml';
            }
          }
        } else if (blob.type === 'application/pdf' && !fileName.toLowerCase().endsWith('.pdf')) {
          if (fileName.toLowerCase().endsWith('_pdf')) {
            fileName = fileName.substring(0, fileName.length - 4) + '.pdf';
          } else {
            fileName += '.pdf';
          }
        }

        // Crear un objeto URL para la descarga
        const newObjectURL = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = newObjectURL;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Liberar memoria
        URL.revokeObjectURL(newObjectURL);
      }
    } catch (error) {
      console.error("Error al descargar el archivo:", error);
    }
  };

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3} alignItems="center">
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel id="date-filter-label">Fecha</InputLabel>
            <Select
              labelId="date-filter-label"
              value={dateFilter}
              label="Fecha"
              onChange={handleDateFilterChange}
            >
              <MenuItem value="all">Todo</MenuItem>
              <MenuItem value="this-month">Este mes</MenuItem>
              <MenuItem value="last-month">Mes pasado</MenuItem>
              <MenuItem value="last-7-days">Últimos 7 días</MenuItem>
              <MenuItem value="custom">Personalizado</MenuItem>
            </Select>
          </FormControl>

          {dateFilter === 'custom' && (
            <>
              <DatePicker
                label="Fecha inicio"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{ textField: { size: 'small' } }}
              />
              <DatePicker
                label="Fecha fin"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{ textField: { size: 'small' } }}
              />
            </>
          )}
        </Stack>
      </LocalizationProvider>

      <TableContainer
        className="table-content"
        components={{
          Container: props => <Paper {...props} elevation={0} />
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={true}
                  direction={order}
                  onClick={createSortHandler}
                >
                  Fecha
                  <Box mt={2} component="span">
                    {order === "desc"
                      ? <KeyboardArrowDownIcon />
                      : <KeyboardArrowUpIcon />
                    }
                  </Box>
                </TableSortLabel>
              </TableCell>
              <TableCell>Tipo de operación</TableCell>
              <TableCell>Monto</TableCell>
              <TableCell>Comprobante</TableCell>
              <TableCell>Servicio</TableCell>
              <TableCell>Estatus</TableCell>
              <TableCell>Factura</TableCell>
              <TableCell>Empresa</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {
              (
                rowsPerPage > 0 ?
                  sortedAndPaginatedOperaciones.slice((page - 1) * rowsPerPage, page * rowsPerPage) :
                  sortedAndPaginatedOperaciones
              ).map((row, i) => {
                const formatoMonto = row.monto.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                return (
                  <TableRow
                    key={i}
                    sx={{ "& td, & th": { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {moment(row.date).format("YYYY / MM / DD")}
                    </TableCell>
                    <TableCell>
                      {
                        isEmpty(row.nombreServicio) ? (
                          <center>
                            {row.type}
                          </center>
                        ) : (
                          <center>
                            Servicio
                          </center>
                        )
                      }
                    </TableCell>
                    <TableCell>
                      <center>
                        $ {formatoMonto}
                      </center>
                    </TableCell>
                    <TableCell>
                      {
                        row.metodo === "Transferencia" ? (
                          <Stack direction="row" spacing={0} justifyContent="center" alignItems="center">
                            <Button onClick={() => onSelect(row)}>
                              Ver
                            </Button>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleDownload(row.file)}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        ) : row.metodo === "paypal" ? (
                          <center>
                            <Typography color="primary">
                              Paypal
                            </Typography>
                          </center>
                        ) : row.metodo === "mercadopago" ? (
                          <center>
                            <Typography color="primary">
                              Mercado Pago
                            </Typography>
                          </center>
                        ) : row.metodo === "googlePay" ? (
                          <center>
                            <Typography color="primary">
                              Google Pay
                            </Typography>
                          </center>
                        ) : row.metodo === "solicitud" ? (
                          <center>
                            {(!isEmpty(row.factura_pdf) || !isEmpty(row.factura_xml)) ? (
                              (!isEmpty(row.extraData?.comprobante) && row.status !== 2) ? (
                                <Stack direction="row" spacing={0} justifyContent="center" alignItems="center">
                                  <Button onClick={() => onSelect(row)}>
                                    Ver
                                  </Button>
                                  <IconButton size="small" color="primary"
                                    onClick={() => handleDownload(row.extraData.comprobante)}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              ) : (
                                <Button 
                                  size="small" 
                                  variant="contained" 
                                  color={row.status === 2 ? "error" : "primary"}
                                  onClick={() => setComprobanteItem(row)}
                                  sx={{ whiteSpace: 'nowrap', px: 2 }}
                                >
                                  {!isEmpty(row.extraData?.comprobante) ? 'Volver a subir' : 'Subir Comprobante'}
                                </Button>
                              )
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                Pendiente de factura
                              </Typography>
                            )}
                          </center>
                        ) : (
                          <center>
                            <Typography color="primary">
                              Apple Pay
                            </Typography>
                          </center>
                        )
                      }
                    </TableCell>
                    <TableCell>
                      {
                        (isEmpty(row.nombreServicio) && isEmpty(row.servicioEmpresa)) ?
                          'Sin servicio' : isEmpty(row.nombreServicio) ?
                            row.servicioEmpresa :
                            row.nombreServicio
                      }
                    </TableCell>
                    <TableCell>
                      {getBadge(row.status)}
                    </TableCell>
                    <TableCell>
                      {(!isEmpty(row.factura_pdf) || !isEmpty(row.factura_xml)) ? (
                        <Stack direction="row" spacing={1} justifyContent="center">
                          {!isEmpty(row.factura_pdf) && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleDownload(row.factura_pdf)}
                            >
                              PDF
                            </Button>
                          )}
                          {!isEmpty(row.factura_xml) && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleDownload(row.factura_xml)}
                            >
                              XML
                            </Button>
                          )}
                        </Stack>
                      ) : (
                        <center>
                          <Typography variant="caption">
                            Sin factura
                          </Typography>
                        </center>
                      )}
                    </TableCell>
                    <TableCell>
                      <center>
                        <Typography color="primary">
                          {
                            row.metodo === "solicitud" ? (
                              row.extraData?.razon_social || 'No aplica'
                            ) : row.empresa && !isEmpty(empresas) ? (
                              empresas.find(e => e._id === row.empresa)?.razon || 'No aplica'
                            ) : row.nombreEmpresa || 'No aplica'
                          }
                        </Typography>
                      </center>
                    </TableCell>
                  </TableRow>
                )
              }
              )
            }
          </TableBody>
        </Table>
      </TableContainer>
      <Stack className="pagination" spacing={2} mt={3}>
        <Pagination
          sx={{ width: "100%" }}
          color="primary"
          shape="rounded"
          count={Math.ceil(sortedAndPaginatedOperaciones.length / rowsPerPage)}
          onChange={(event, value) => handleChangePage(value)}
          page={page}
          size="large"
        />
      </Stack>

      <UploadComprobanteModal
        open={!!comprobanteItem}
        handleClose={() => setComprobanteItem(null)}
        item={comprobanteItem}
      />
    </>
  )
}

TableInv.propTypes = {
  onSelect: PropTypes.func.isRequired,
}

export default TableInv;